import React, { useEffect, useCallback, useState, useRef } from "react";
import * as d3 from "d3";
import { useMap, SVGOverlay } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import useWindowDims from "../hooks/WindowDims";
import useMapZoom from "../hooks/MapZoom";
import L from "leaflet";
import uuid from "react-uuid";
import { toggleDraw } from "../reducers/DrawSlice";
import { setItem } from "../reducers/ItemSlice";
import { setTooltip2 } from "../reducers/Tooltip2Slice";
import { setTooltip1 } from "../reducers/Tooltip1Slice";
import { useSelector, useDispatch } from "react-redux";
import "./HotelMap.css"
import ToolTip from "./ToolTip"
import StopsTooltip from "./StopsTooltip"
import '@fortawesome/fontawesome-svg-core/styles.css';
import { config } from '@fortawesome/fontawesome-svg-core';
import { toggleMapReady } from '../reducers/MapReadySlice'
import { GridLoader, PropagateLoader } from "react-spinners";
config.autoAddCss = true;


const HotelMap = () => {
    const map = useMap();

    const draw = useSelector((state) => state.draw.value);
    const data = useSelector((state) => state.data.value);
    const colors = useSelector((state) => state.colors.value);
    const item = useSelector((state) => state.item.value);
    const mapReady = useSelector((state) => state.mapReady.value)
    const param = useSelector((state) => state.param.value);
    const tooltip1 = useSelector((state) => state.tooltip1.value);
    const tooltip2 = useSelector((state) => state.tooltip2.value);
    const dispatch = useDispatch()
    const { width, height } = useWindowDims()
    const [zoom, center, bounds] = useMapZoom();
    const [prevParam, setPrevParam] = useState(param)
    const [zoomShow, setZoomShow] = useState(0)
    const geoPath = useRef();
    const textScale = useRef();
    const markerWidth = useRef();
    const svgRef = useRef();
    const intersects = (a, b) => {
        const s = new Set(b);
        return [...new Set(a)].some(x => s.has(x));
    };

    useEffect(() => {
        textScale.current = d3.scaleLinear()
            .domain([7, 25]) // Input: Leaflet zoom levels
            .range([0.3, 2.0]);
    })

    useEffect(() => {
        markerWidth.current = d3.scaleLinear()
            .domain([7, 18]) // Input: Leaflet zoom levels
            .range([10, 80]);
    })


    useEffect(() => {
        function projectPoint(x, y) {
            const point = map.latLngToLayerPoint(new L.LatLng(y, x));
            this.stream.point(point.x, point.y);
        }

        const transform = d3.geoTransform({
            point: projectPoint,
        });

        geoPath.current = d3.geoPath().projection(transform);
    }, [map]);

    const drawCallback = useCallback(() => {

        const targetBounds = []
        data[0][param].map(d => targetBounds.push([d.lon, d.lat]))

        const lon = d3.extent(targetBounds.map(c => c[0]))
        const lat = d3.extent(targetBounds.map(c => c[1]))

        const newBounds = L.latLngBounds([
            [lat[1], lon[1]],
            [lat[0], lon[0]]
        ]);

        d3.selectAll("#group1-g").remove();
        d3.selectAll("#group2-g").remove();
        d3.selectAll("#group3-g").remove();
        d3.selectAll("#group5-g").remove();

        map.flyToBounds(newBounds)

        const group2 = d3
            .select(".leaflet-image-layer")
            .append("g")
            .attr("id", "group2-g");

        const group2Join = group2.selectAll("image").data(data[0][param]);

        group2Join
            .enter()
            .append("image")
            .attr("key", (d) => uuid())
            .attr("x", function (d) {

                return map.latLngToLayerPoint([d.lat, d.lon]).x - (markerWidth.current(zoom) / 2);
            })
            .attr("y", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).y - (markerWidth.current(zoom) / 2);
            })
            .attr("width", markerWidth.current(zoom))
            .attr("height", markerWidth.current(zoom))
            .attr("href", (d) => {
                if (item.names.includes(d.name)) {
                    return "data/ebs-dark-blue-icon.png"
                }
                else {
                    return "data/ebs-light-blue-icon.png"
                }

            })
            .style("opacity", (d) => {
                if (item.names.includes(d.name)) {
                    return 0.85
                }
                else {
                    return 0.80
                }

            })



        const group3 = d3
            .select(".leaflet-image-layer")
            .append("g")
            .attr("id", "group3-g");

        const group3Join = group3.selectAll("text").data(data[0][param]);

        group3Join
            .enter()

            .append("text")
            .attr("x", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).x
            })
            .attr("y", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).y
            })
            .text(d => d.name.split(" -")[0].replace("מלון ", ""))

            .style("font-size", "1.0em")
            .attr("textLength", markerWidth.current(zoom) - 10)
            .attr("lengthAdjust", "spacingAndGlyphs")
            .attr("key", (d) => uuid())
            .attr("fill", (d) => {
                if (item.names.includes(d.name)) {
                    return "white"
                }
                else {
                    return colors["darkgray"]
                }

            })
            .style("text-anchor", "middle")



        const group5 = d3
            .select(".leaflet-image-layer")
            .append("g")
            .attr("id", "group5-g");

        const group5Join = group5.selectAll("rect").data(data[0][param]);


        group5Join
            .enter()
            .append("rect")
            .attr("x", function (d) {

                return map.latLngToLayerPoint([d.lat, d.lon]).x - (markerWidth.current(zoom) / 2);
            })
            .attr("y", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).y - (markerWidth.current(zoom) / 2);
            })
            .attr("width", markerWidth.current(zoom))
            .attr("height", markerWidth.current(zoom))
            .attr("stroke", "transparent")
            .attr("fill", "transparent")


        const group1 = d3
            .select(".leaflet-image-layer")
            .append("g")
            .attr("id", "group1-g");

        const group1Join = group1.selectAll("text").data(data[1]);

        group1Join
            .enter()
            .append("text")
            .attr("font-size", "1.5em")
            .attr("key", (d) => uuid())
            .attr("x", function (d) {

                return map.latLngToLayerPoint([d.lat, d.lon]).x;
            })
            .attr("y", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).y;
            })
            .attr("fill", colors["red"])
            .attr("class", "fa")
            .text('\uf207')

            dispatch(toggleMapReady(true));





    }, [item, zoom, markerWidth, map, colors, zoomShow, data, param,dispatch]);

    const updateCallback = useCallback(() => {
        // if (!flying){
        console.log("update", zoom)
        const svg = d3.select(".leaflet-image-layer");

        const g1 = svg.selectAll("#group1-g");
        const g2 = svg.selectAll("#group2-g");
        const g3 = svg.selectAll("#group3-g");
        const g5 = svg.selectAll("#group5-g");

        const elem1 = g1.selectAll("text");
        const elem2 = g2.selectAll("image");
        const elem3 = g3.selectAll("text");
        const elem5 = g5.selectAll("rect");

        const topLeft = map.latLngToLayerPoint(bounds.getNorthWest());
        const bottomRight = map.latLngToLayerPoint(bounds.getSouthEast());
        const w = bottomRight.x - topLeft.x
        const h = bottomRight.y - topLeft.y
        svg
            // .attr("bounds", bounds)

            .attr("viewBox", "0 0 " + w + " " + h + "")
            .attr("width", w)
            .attr("height", h)
            .attr("top", topLeft.x + 'px')
            .attr("left", topLeft.y + 'px')

        g1.attr("transform", "translate(" + -topLeft.x + "," + -topLeft.y + ")")
        g2.attr("transform", "translate(" + -topLeft.x + "," + -topLeft.y + ")")
        g3.attr("transform", "translate(" + -topLeft.x + "," + -topLeft.y + ")")
        g5.attr("transform", "translate(" + -topLeft.x + "," + -topLeft.y + ")")

        elem1

            .attr("x", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).x
            })
            .attr("y", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).y
            })
            .style("fill", (d) => {

                if (intersects(item.idxs, d.idxs)) {
                    return colors["red"]
                }
                else {
                    return "transparent"
                }
            }
            )

        elem2

            .attr("x", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).x - (markerWidth.current(zoom) / 2)
            })
            .attr("y", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).y - (markerWidth.current(zoom) / 2)
            })
            .attr("width", markerWidth.current(zoom))
            .attr("height", markerWidth.current(zoom))
            .attr("href", (d) => {
                if (item.names.includes(d.name)) {
                    return "data/ebs-dark-blue-icon.png"
                }
                else {
                    return "data/ebs-light-blue-icon.png"
                }

            })
            .style("opacity", (d) => {
                if (item.names.includes(d.name)) {
                    return 0.85
                }
                else {
                    return 0.80
                }
            })


        elem3
            .attr("x", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).x
            })
            .attr("y", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).y
            })
            .attr("textLength", markerWidth.current(zoom) - 10)
            .attr("lengthAdjust", "spacingAndGlyphs")
            .attr("fill", (d) => {
                if (item.names.includes(d.name)) {
                    return "white"
                }
                else {
                    return colors["darkgray"]
                }

            })
            // .style("opacity", (d) => {
            //     if (item.names.includes(d.name)) {
            //         return 1
            //     }
            //     else {
            //         return 1
            //     }
            // })
        elem5
            .attr("x", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).x - (markerWidth.current(zoom) / 2)
            })
            .attr("y", function (d) {
                return map.latLngToLayerPoint([d.lat, d.lon]).y - (markerWidth.current(zoom) / 2)
            })
            .attr("width", markerWidth.current(zoom))
            .attr("height", markerWidth.current(zoom))






    }, [zoomShow,
        item,
        markerWidth,
        map,
        zoom,
        bounds,
        colors,

    ]);

    const eventCallback = useCallback(() => {
        // if (draw ) {

        const svg = d3.select(".leaflet-image-layer");
        const g5 = svg.selectAll("#group5-g");
        const elem5 = g5.selectAll("rect");
        elem5
            .on("click", (event, d) => {
                const tmpData = data[0][param].filter(h => h.name == d.name)

                dispatch(setItem({
                    type: "hotel",
                    names: [d.name],
                    data: tmpData,
                    idxs: [tmpData[0].idx]
                }))

                event.stopPropagation();
            })

        // }


    }, [data, dispatch, param])

    const tooltipCallback = useCallback(() => {
        // if (draw) {

        const svg = d3.select(".leaflet-image-layer");
        const g5 = svg.selectAll("#group5-g");
        const elem5 = g5.selectAll("rect");


        elem5
            .on("mouseenter", (event, d) => {

                dispatch(setTooltip2({
                    show: true,
                    x: event.pageX,
                    y: event.pageY,
                    data: d,

                }))


                event.stopPropagation()
            })


    }, [dispatch])

    const StopsTooltipCallback = useCallback(() => {
        // if (draw) {

        const svg = d3.select(".leaflet-image-layer");
        const g1 = svg.selectAll("#group1-g");
        const elem1 = g1.selectAll("text");


        elem1
            .on("mouseenter", (event, d) => {

                dispatch(setTooltip1({
                    show: true,
                    x: event.pageX,
                    y: event.pageY,
                    data: d,

                }))


                event.stopPropagation()
            })
            .on("mouseout", (event, d) => {

                dispatch(setTooltip1({
                    show: false,
                    x: 0,
                    y: 0,
                    data: {},

                }))


                event.stopPropagation()
            })


    }, [dispatch])

    const rightClickCallback = useCallback(() => {

        const svg = d3.select(".leaflet-image-layer");

        map
            .on("contextmenu", (event) => {


                dispatch(setTooltip2({
                    show: false,
                    x: 0,
                    y: 0,
                    data: {},

                }))


            })


    }, [map, dispatch])




    useEffect(() => {
        if (
            (!draw && Object.keys(data[0]).length > 0 && data[1].length > 0)

        ) {

            
            drawCallback();
            updateCallback();
            dispatch(toggleDraw());
          

        } else if (draw) {
            updateCallback();
            eventCallback();
            tooltipCallback();
            rightClickCallback();
            StopsTooltipCallback();
            // redrawCallback()
            //  drawCallback();
        }
    }, [draw,
        drawCallback,
        updateCallback,
        eventCallback,
        tooltipCallback,
        StopsTooltipCallback,
        rightClickCallback,
        item.data,
        dispatch,
        data]);

    useEffect(() => {
        if (
            (param != prevParam)
        ) {
            drawCallback();
            setPrevParam(param)
        }
    }, [drawCallback, param, prevParam,]);



    return (
     
         
                <>
                    <SVGOverlay bounds={bounds} ref={svgRef}>
                    </SVGOverlay>
                    {tooltip2.show &&
                        (
                            <ToolTip tooltip={tooltip2} />
                        )}

                    {tooltip1.show &&
                        (
                            <StopsTooltip tooltip={tooltip1} />
                        )}
                </>
           
    );
};
export default HotelMap;